import React from 'react';
import { Box, Typography, Grid, Link as MuiLink, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Container } from './Container';
import { setModal } from '../../Modules/Common/App';
import { CookieConsentModal } from './CookieConsentModal';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const StyledFooter = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark'
        ? theme.palette.background.default
        : theme.palette.grey[900],
    color: theme.palette.common.white,
    fontWeight: 300,
    padding: theme.spacing(8, 0, 4),
    marginTop: 'auto',
    transition: 'background-color 0.3s ease, color 0.3s ease',
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(6, 0, 3),
    },
    '& .footer-brand': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        textAlign: 'left',
        gap: 0,
        [theme.breakpoints.down('md')]: {
            alignItems: 'center',
            textAlign: 'center',
        },
        '& .footer-logo': {
            display: 'inline-block',
            marginBottom: theme.spacing(3),
            '& img': {
                filter: 'brightness(0) invert(1)',
                height: 50,
                width: 50,
            },
        },
        '& .footer-logo-link': {
            display: 'inline-block',
        },
        '& .footer-description': {
            color: theme.palette.grey[500],
            fontSize: '0.875rem',
            fontWeight: 300,
            marginBottom: theme.spacing(3),
            lineHeight: 1.6,
        },
    },
    '& .footer-links-section': {
        marginTop: theme.spacing(6),
        marginBottom: theme.spacing(3),
        paddingTop: theme.spacing(4),
        borderTop: `1px solid ${theme.palette.grey[800]}`,
        '& .MuiGrid-item': {
            textAlign: 'left',
            [theme.breakpoints.down('sm')]: {
                textAlign: 'center',
            },
            '& h3, & .footer-column-title': {
                color: theme.palette.primary.main,
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: theme.spacing(3),
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
            },
            '& ul': {
                listStyle: 'none',
                padding: 0,
                margin: 0,
            },
            '& li': {
                marginBottom: theme.spacing(1.5),
            },
            '& a': {
                color: theme.palette.grey[400],
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'color 0.3s ease',
                '&:hover': {
                    color: theme.palette.common.white,
                },
            },
        },
    },
    '& .social-links': {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
        [theme.breakpoints.down('lg')]: {
            justifyContent: 'center',
        },
        [theme.breakpoints.up('lg')]: {
            justifyContent: 'flex-end',
            marginTop: 0,
        },
        '@keyframes socialIconDrop': {
            '0%': {
                opacity: 0,
                transform: 'translateY(-20px) scale(0.8)',
            },
            '100%': {
                opacity: 1,
                transform: 'translateY(0) scale(1)',
            },
        },
        '& .social-link': {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '3rem',
            height: '3rem',
            position: 'relative',
            opacity: 0,
            transform: 'translateY(-20px) scale(0.8)',
            color: theme.palette.grey[400],
            fontWeight: 300,
            fontSize: '1.1rem',
            transition: 'all 0.3s ease',
            borderRadius: theme.spacing(1.5),
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            textDecoration: 'none',
            overflow: 'hidden',
            animation: 'socialIconDrop 0.6s ease-out forwards',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0,
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                borderRadius: theme.spacing(1.5),
                transition: 'opacity 0.3s ease',
                zIndex: 0,
            },
            '&:nth-of-type(1)': {
                animationDelay: '0.1s',
            },
            '&:nth-of-type(2)': {
                animationDelay: '0.2s',
            },
            '&:nth-of-type(3)': {
                animationDelay: '0.3s',
            },
            '&:nth-of-type(4)': {
                animationDelay: '0.4s',
            },
            '&:hover': {
                color: theme.palette.common.white,
                transform: 'translateY(-2px) scale(1)',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.04) 100%)',
                '&::before': {
                    opacity: 1,
                },
            },
            '& .MuiSvgIcon-root': {
                fontSize: '1.1rem',
                position: 'relative',
                zIndex: 1,
            },
            '& .x-icon': {
                fontSize: '1.1rem',
                fontWeight: 300,
                display: 'inline-block',
                position: 'relative',
                zIndex: 1,
            },
        },
    },
    '& .footer-bottom': {
        borderTop: `1px solid ${theme.palette.grey[800]}`,
        paddingTop: theme.spacing(2),
        marginTop: theme.spacing(3),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        textAlign: 'center',
        [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            textAlign: 'left',
        },
        '& .copyright': {
            color: theme.palette.grey[400],
            fontSize: '0.875rem',
            fontWeight: 300,
            margin: 0,
            '& .version-info': {
                marginLeft: theme.spacing(1),
                color: theme.palette.grey[500],
                fontSize: '0.75rem',
            },
        },
        '& .legal-links': {
            display: 'flex',
            justifyContent: 'center',
            gap: theme.spacing(2),
            '& a, & button': {
                color: theme.palette.grey[400],
                fontSize: '0.875rem',
                textDecoration: 'none',
                transition: 'color 0.3s ease',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                '&:hover': {
                    color: theme.palette.common.white,
                    textDecoration: 'underline',
                },
            },
            '& .manage-preferences-wrapper': {
                display: 'inline-flex',
                alignItems: 'center',
                gap: theme.spacing(0.5),
            },
            '& .recording-dot': {
                fontSize: '0.5rem',
                color: '#ff4444',
                animation: 'blink 2s ease-in-out infinite',
            },
            '@keyframes blink': {
                '0%, 100%': {
                    opacity: 0.4,
                },
                '50%': {
                    opacity: 1,
                },
            },
        },
    },
}));

const FOOTER_LINKS = {
    mainWebsite: [
        { label: 'Home', href: 'https://www.dgtia.com/' },
        { label: 'Services', href: 'https://www.dgtia.com/services/' },
        { label: 'Products', href: 'https://www.dgtia.com/products/' },
        { label: 'Company', href: 'https://www.dgtia.com/company/' },
        { label: 'Contact', href: 'https://www.dgtia.com/contact/' },
    ],
    developerTools: [
        { label: 'JSON Formatter', route: '/json-formatter' },
        { label: 'Hash Generator', route: '/hash-generator' },
        { label: 'UUID Generator', route: '/uuid-generator' },
        { label: 'URL Builder', route: '/url-builder' },
        { label: 'Base64 Encoder/Decoder', route: '/base64-encoder-decoder' },
    ],
    mediaTools: [
        { label: 'Image Studio', route: '/image-studio' },
        { label: 'Base64 to Image', route: '/base64-to-image' },
    ],
    textProcessing: [
        { label: 'Password Generator', route: '/password-generator' },
        { label: 'URL Encoder/Decoder', route: '/url-encoder-decoder' },
        { label: 'QR Code Generator', route: '/qr-code-generator' },
        { label: 'Text Case Converter', route: '/text-case-converter' },
        { label: 'Unix Timestamp Converter', route: '/unix-timestamp-converter' },
        { label: 'ULID Generator', route: '/ulid-generator' },
        { label: 'GUID Generator', route: '/guid-generator' },
    ],
};

export const Footer = () => {
    const dispatch = useDispatch();
    const consent = useSelector((state) => state.app?.consent);
    const currentYear = new Date().getFullYear();
    const version = 'v1.0.57';

    const handleManagePreferences = () => {
        dispatch(setModal({
            component: <CookieConsentModal />,
            open: true,
            size: 'md',
        }));
    };

    return (
        <StyledFooter component="footer" className="main-footer">
            <Container>
                <Grid container spacing={3} alignItems="flex-start">
                    <Grid item xs={12} md={8} lg={8}>
                        <Box className="footer-brand">
                            <MuiLink
                                component={Link}
                                to="/"
                                className="footer-logo footer-logo-link"
                            >
                                <Box
                                    component="img"
                                    src="/images/logo.svg"
                                    alt="DGTIA Tools Logo"
                                />
                            </MuiLink>
                            <Typography variant="body2" className="footer-description">
                                DGTIA provides comprehensive web analysis tools to help businesses optimize their online presence. Analyze SEO, performance, security, accessibility, and mobile compatibility with our free, instant insights.
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={4} lg={4}>
                        <Box className="social-links">
                            <IconButton
                                component="a"
                                href="https://linkedin.com/company/dgtia"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="LinkedIn"
                                className="social-link"
                            >
                                <LinkedInIcon />
                            </IconButton>
                            <IconButton
                                component="a"
                                href="https://facebook.com/dgtia"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook"
                                className="social-link"
                            >
                                <FacebookIcon />
                            </IconButton>
                            <IconButton
                                component="a"
                                href="https://twitter.com/dgtiaofficial"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="X (formerly Twitter)"
                                className="social-link"
                            >
                                <Box component="span" className="x-icon">𝕏</Box>
                            </IconButton>
                            <IconButton
                                component="a"
                                href="https://instagram.com/thedgtia"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram"
                                className="social-link"
                            >
                                <InstagramIcon />
                            </IconButton>
                        </Box>
                    </Grid>
                </Grid>

                <Box className="footer-links-section">
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={3} lg={3}>
                            <Typography component="h3" variant="body2" className="footer-column-title">
                                DGTIA
                            </Typography>
                            <ul>
                                {FOOTER_LINKS.mainWebsite.map((link) => (
                                    <li key={link.href}>
                                        <MuiLink
                                            href={link.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {link.label}
                                        </MuiLink>
                                    </li>
                                ))}
                            </ul>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} lg={3}>
                            <Typography component="h3" variant="body2" className="footer-column-title">
                                Developer Tools
                            </Typography>
                            <ul>
                                {FOOTER_LINKS.developerTools.map((tool) => (
                                    <li key={tool.route}>
                                        <MuiLink
                                            component={Link}
                                            to={tool.route}
                                        >
                                            {tool.label}
                                        </MuiLink>
                                    </li>
                                ))}
                            </ul>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} lg={3}>
                            <Typography component="h3" variant="body2" className="footer-column-title">
                                Media Tools
                            </Typography>
                            <ul>
                                {FOOTER_LINKS.mediaTools.map((tool) => (
                                    <li key={tool.route}>
                                        <MuiLink
                                            component={Link}
                                            to={tool.route}
                                        >
                                            {tool.label}
                                        </MuiLink>
                                    </li>
                                ))}
                            </ul>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3} lg={3}>
                            <Typography component="h3" variant="body2" className="footer-column-title">
                                Text Processing
                            </Typography>
                            <ul>
                                {FOOTER_LINKS.textProcessing.map((tool) => (
                                    <li key={tool.route}>
                                        <MuiLink
                                            component={Link}
                                            to={tool.route}
                                        >
                                            {tool.label}
                                        </MuiLink>
                                    </li>
                                ))}
                            </ul>
                        </Grid>
                    </Grid>
                </Box>

                <Box className="footer-bottom">
                    <Box>
                        <Typography variant="body2" className="copyright">
                            © {currentYear} DGTIA Tools. All rights reserved.
                            <Box component="span" className="version-info">{version}</Box>
                        </Typography>
                    </Box>
                    <Box className="legal-links">
                        <Box component="span" className="manage-preferences-wrapper">
                            {consent?.status === 'accepted' && (
                                <FiberManualRecordIcon className="recording-dot" />
                            )}
                            <MuiLink
                                component="button"
                                onClick={handleManagePreferences}
                            >
                                Manage Preferences
                            </MuiLink>
                        </Box>
                        <MuiLink
                            href="https://dgtia.com/privacy-policy/"
                            target="_blank"
                            rel="noopener"
                        >
                            Privacy Policy
                        </MuiLink>
                        <MuiLink
                            href="https://dgtia.com/terms-of-service/"
                            target="_blank"
                            rel="noopener"
                        >
                            Terms of Service
                        </MuiLink>
                        <MuiLink
                            href="https://dgtia.com/cookie-policy/"
                            target="_blank"
                            rel="noopener"
                        >
                            Cookie Policy
                        </MuiLink>
                    </Box>
                </Box>
            </Container>
        </StyledFooter>
    );
};

export default Footer;
